import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, useState } from "react";
import { fetchExchangeRates, forceFetchExchangeRates, getCachedRateInfo } from "../services/currencyApi";
import * as supabaseApi from "../services/supabaseApi";

// ============================================================================
// CONSTANTES
// ============================================================================
const INITIAL_RECURRING_INCOME_TEMPLATES = [
  { id: "inc_t1", name: "Sueldo", amount: 3500, active: true },
  { id: "inc_t2", name: "Freelance", amount: 0, active: false }
];

const INITIAL_RECURRING_TEMPLATES = [];

export const CURRENCIES = { PEN: "S/", USD: "$", COP: "COL$", ARS: "AR$", CLP: "CLP$", VEF: "Bs" };

export const CURRENCY_OPTIONS = [
  { code: "PEN", symbol: "S/", label: "Soles (Perú)" },
  { code: "USD", symbol: "$", label: "Dólares (USA)" },
  { code: "COP", symbol: "COL$", label: "Pesos (Colombia)" },
  { code: "ARS", symbol: "AR$", label: "Pesos (Argentina)" },
  { code: "CLP", symbol: "CLP$", label: "Pesos (Chile)" },
  { code: "VEF", symbol: "Bs", label: "Bolívares (Venezuela)" }
];

const DEFAULT_EXCHANGE_RATES = { PEN: 3.75, USD: 1, COP: 4100, ARS: 1000, CLP: 950, VEF: 36 };

const DEFAULT_ENABLED_CURRENCIES = ["PEN", "USD"];

const DEFAULT_STATE = {
  currency: "PEN",
  currencySymbol: "S/",
  usdToPenRate: 3.75,
  exchangeRates: DEFAULT_EXCHANGE_RATES,
  enabledCurrencies: DEFAULT_ENABLED_CURRENCIES,
  showAmounts: true,
  months: [],
  incomes: [],
  expenses: [],
  recurringTemplates: INITIAL_RECURRING_TEMPLATES,
  recurringIncomeTemplates: INITIAL_RECURRING_INCOME_TEMPLATES,
  goals: [],
  contributions: [],
  customCategories: [],
};

const getInitialState = () => ({ ...DEFAULT_STATE });

const generateId = (prefix = "id") => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// ============================================================================
// REDUCER
// ============================================================================
const ensureRecurringIncomeTemplates = (arr) =>
  Array.isArray(arr) ? arr : INITIAL_RECURRING_INCOME_TEMPLATES;

const controlCashReducer = (state, action) => {
  let newState;
  const recurringIncomeTemplates = ensureRecurringIncomeTemplates(state.recurringIncomeTemplates);

  switch (action.type) {
    case "HYDRATE":
      return { ...DEFAULT_STATE, ...action.payload };

    // ========== MONTHS ==========
    case "CREATE_MONTH":
      newState = {
        ...state,
        months: [...state.months, { id: action.payload.id || generateId("month"), ...action.payload }]
      };
      break;

    case "DELETE_MONTH":
      newState = {
        ...state,
        months: state.months.filter(m => m.id !== action.payload),
        incomes: state.incomes.filter(i => i.monthId !== action.payload),
        expenses: state.expenses.filter(e => e.monthId !== action.payload)
      };
      break;

    // ========== INCOMES ==========
    case "ADD_INCOME":
      newState = {
        ...state,
        incomes: [...state.incomes, { id: action.payload.id || generateId("income"), ...action.payload }]
      };
      break;

    case "UPDATE_INCOME":
      newState = {
        ...state,
        incomes: state.incomes.map(i =>
          i.id === action.payload.incomeId 
            ? { ...i, name: action.payload.name, amount: action.payload.amount } 
            : i
        )
      };
      break;

    case "DELETE_INCOME":
      newState = {
        ...state,
        incomes: state.incomes.filter(i => i.id !== action.payload.incomeId)
      };
      break;

    // ========== EXPENSES ==========
    case "ADD_EXPENSE":
      newState = {
        ...state,
        expenses: [...state.expenses, { id: action.payload.id || generateId("expense"), ...action.payload }]
      };
      break;

    case "UPDATE_EXPENSE":
      newState = {
        ...state,
        expenses: state.expenses.map(e =>
          e.id === action.payload.id ? { ...e, ...action.payload.patch } : e
        )
      };
      break;

    case "DELETE_EXPENSE":
      newState = {
        ...state,
        expenses: state.expenses.filter(e => e.id !== action.payload)
      };
      break;

    case "TOGGLE_EXPENSE_PAID":
      newState = {
        ...state,
        expenses: state.expenses.map(e =>
          e.id === action.payload
            ? {
                ...e,
                status: e.status === "PENDING" ? "PAID" : "PENDING",
                paidAt: e.status === "PENDING" ? new Date().toISOString() : null
              }
            : e
        )
      };
      break;

    // ========== RECURRING TEMPLATES ==========
    case "CREATE_RECURRING_TEMPLATE":
      newState = {
        ...state,
        recurringTemplates: [
          ...state.recurringTemplates,
          { id: action.payload.id || generateId("template"), ...action.payload }
        ]
      };
      break;

    case "UPDATE_RECURRING_TEMPLATE":
      newState = {
        ...state,
        recurringTemplates: state.recurringTemplates.map(t =>
          t.id === action.payload.id ? { ...t, ...action.payload.patch } : t
        )
      };
      break;

    case "DELETE_RECURRING_TEMPLATE":
      newState = {
        ...state,
        recurringTemplates: state.recurringTemplates.filter(t => t.id !== action.payload)
      };
      break;

    case "TOGGLE_RECURRING_TEMPLATE":
      newState = {
        ...state,
        recurringTemplates: state.recurringTemplates.map(t =>
          t.id === action.payload ? { ...t, active: !t.active } : t
        )
      };
      break;

    // ========== RECURRING INCOME TEMPLATES ==========
    case "CREATE_RECURRING_INCOME_TEMPLATE":
      newState = {
        ...state,
        recurringIncomeTemplates: [
          ...recurringIncomeTemplates,
          { id: action.payload.id || generateId("inc_template"), ...action.payload }
        ]
      };
      break;

    case "UPDATE_RECURRING_INCOME_TEMPLATE":
      newState = {
        ...state,
        recurringIncomeTemplates: recurringIncomeTemplates.map(t =>
          t.id === action.payload.id ? { ...t, ...action.payload.patch } : t
        )
      };
      break;

    case "DELETE_RECURRING_INCOME_TEMPLATE":
      newState = {
        ...state,
        recurringIncomeTemplates: recurringIncomeTemplates.filter(t => t.id !== action.payload)
      };
      break;

    case "TOGGLE_RECURRING_INCOME_TEMPLATE":
      newState = {
        ...state,
        recurringIncomeTemplates: recurringIncomeTemplates.map(t =>
          t.id === action.payload ? { ...t, active: !t.active } : t
        )
      };
      break;

    // ========== GOALS ==========
    case "CREATE_GOAL":
      newState = {
        ...state,
        goals: [...state.goals, { id: action.payload.id || generateId("goal"), ...action.payload }]
      };
      break;

    case "UPDATE_GOAL":
      newState = {
        ...state,
        goals: state.goals.map(g =>
          g.id === action.payload.id ? { ...g, ...action.payload.patch } : g
        )
      };
      break;

    case "DELETE_GOAL":
      newState = {
        ...state,
        goals: state.goals.filter(g => g.id !== action.payload),
        contributions: state.contributions.filter(c => c.goalId !== action.payload)
      };
      break;

    // ========== CONTRIBUTIONS ==========
    case "ADD_CONTRIBUTION":
      newState = {
        ...state,
        contributions: [
          ...state.contributions,
          { id: action.payload.id || generateId("contribution"), ...action.payload }
        ]
      };
      break;

    case "DELETE_CONTRIBUTION":
      newState = {
        ...state,
        contributions: state.contributions.filter(c => c.id !== action.payload)
      };
      break;

    // ========== CONFIGURACIÓN (MONEDA) ==========
    case "SET_CURRENCY": {
      const code = action.payload === "S/" || action.payload === CURRENCIES.PEN ? "PEN"
        : action.payload === "$" || action.payload === CURRENCIES.USD ? "USD"
        : CURRENCY_OPTIONS.some(o => o.code === action.payload) ? action.payload : "PEN";
      const opt = CURRENCY_OPTIONS.find(o => o.code === code);
      newState = {
        ...state,
        currency: code,
        currencySymbol: opt?.symbol ?? "S/"
      };
      break;
    }

    case "SET_EXCHANGE_RATE":
      newState = {
        ...state,
        usdToPenRate: Math.max(0.01, parseFloat(action.payload) || 3.75),
        exchangeRates: { ...state.exchangeRates, PEN: Math.max(0.01, parseFloat(action.payload) || 3.75) }
      };
      break;

    case "SET_EXCHANGE_RATES": {
      const rates = { ...DEFAULT_EXCHANGE_RATES, ...action.payload };
      newState = {
        ...state,
        exchangeRates: rates,
        usdToPenRate: rates.PEN ?? state.usdToPenRate
      };
      break;
    }

    case "SET_SHOW_AMOUNTS":
      newState = {
        ...state,
        showAmounts: action.payload
      };
      break;

    case "SET_ENABLED_CURRENCIES": {
      const codes = Array.isArray(action.payload) && action.payload.length > 0
        ? action.payload.filter(c => CURRENCY_OPTIONS.some(o => o.code === c))
        : DEFAULT_ENABLED_CURRENCIES;
      // Si la moneda actual ya no está habilitada, cambiar a la primera habilitada
      const currentStillEnabled = codes.includes(state.currency);
      const nextCurrency = currentStillEnabled ? state.currency : (codes[0] || "PEN");
      const opt = CURRENCY_OPTIONS.find(o => o.code === nextCurrency);
      newState = {
        ...state,
        enabledCurrencies: codes,
        currency: nextCurrency,
        currencySymbol: opt?.symbol ?? "S/"
      };
      break;
    }

    // ========== CUSTOM CATEGORIES ==========
    case "CREATE_CUSTOM_CATEGORY":
      newState = {
        ...state,
        customCategories: [
          ...(state.customCategories || []),
          { id: action.payload.id || generateId("cat"), name: action.payload.name }
        ]
      };
      break;

    case "UPDATE_CUSTOM_CATEGORY":
      newState = {
        ...state,
        customCategories: (state.customCategories || []).map(c =>
          c.id === action.payload.id ? { ...c, name: action.payload.name } : c
        )
      };
      break;

    case "DELETE_CUSTOM_CATEGORY": {
      const { id: catId, reassignTo } = action.payload;
      newState = {
        ...state,
        customCategories: (state.customCategories || []).filter(c => c.id !== catId),
        expenses: state.expenses.map(e =>
          e.category === catId ? { ...e, category: reassignTo || e.category } : e
        ),
        recurringTemplates: state.recurringTemplates.map(t =>
          t.category === catId ? { ...t, category: reassignTo || t.category } : t
        )
      };
      break;
    }

    default:
      return state;
  }

  return newState;
};

// ============================================================================
// CONTEXT
// ============================================================================
const ControlCashContext = createContext();

// ============================================================================
// PROVIDER
// ============================================================================
export const ControlCashProvider = ({ children, userId }) => {
  const [state, dispatch] = useReducer(controlCashReducer, undefined, getInitialState);
  const [isDataLoading, setIsDataLoading] = useState(!!userId);
  const [dataError, setDataError] = useState(null);
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const syncDispatchRef = useRef(null);

  // Cargar datos desde Supabase al montar
  useEffect(() => {
    if (!userId) {
      setIsDataLoading(false);
      return;
    }
    setIsDataLoading(true);
    setDataError(null);
    const load = async () => {
      try {
        const [
          settings,
          months,
          incomes,
          expenses,
          recurringTemplates,
          recurringIncomeTemplates,
          goals,
          contributions,
          customCategories,
        ] = await Promise.all([
          supabaseApi.fetchUserSettings(userId),
          supabaseApi.fetchMonths(userId),
          supabaseApi.fetchIncomes(userId),
          supabaseApi.fetchExpenses(userId),
          supabaseApi.fetchRecurringTemplates(userId),
          supabaseApi.fetchRecurringIncomeTemplates(userId),
          supabaseApi.fetchGoals(userId),
          supabaseApi.fetchContributions(userId),
          supabaseApi.fetchCustomCategories(userId),
        ]);

        const legacyToCode = (c) => (c === "S/" || c === CURRENCIES.PEN ? "PEN" : c === "$" || c === CURRENCIES.USD ? "USD" : (c && CURRENCY_OPTIONS.some(o => o.code === c) ? c : "PEN"));
        const savedCode = legacyToCode(settings?.currency);
        const opt = CURRENCY_OPTIONS.find(o => o.code === savedCode);
        let ratesFromDb = settings?.exchange_rates;
        if (typeof ratesFromDb === "string") {
          try { ratesFromDb = JSON.parse(ratesFromDb); } catch { ratesFromDb = null; }
        }
        const exchangeRates = {
          ...DEFAULT_EXCHANGE_RATES,
          ...(ratesFromDb && typeof ratesFromDb === "object" ? ratesFromDb : {}),
          PEN: parseFloat(ratesFromDb?.PEN ?? settings?.usd_to_pen_rate ?? 3.75) || 3.75
        };
        let enabledCurrencies = DEFAULT_ENABLED_CURRENCIES;
        const ecRaw = settings?.enabled_currencies;
        if (Array.isArray(ecRaw) && ecRaw.length > 0) {
          const valid = ecRaw.filter(c => CURRENCY_OPTIONS.some(o => o.code === c));
          if (valid.length > 0) enabledCurrencies = valid;
        }
        const hydrateState = {
          currency: opt ? savedCode : "PEN",
          currencySymbol: opt?.symbol ?? "S/",
          usdToPenRate: parseFloat(exchangeRates.PEN ?? settings?.usd_to_pen_rate ?? 3.75),
          exchangeRates,
          enabledCurrencies,
          showAmounts: settings?.show_amounts ?? true,
          months,
          incomes,
          expenses,
          recurringTemplates: recurringTemplates?.length ? recurringTemplates : INITIAL_RECURRING_TEMPLATES,
          recurringIncomeTemplates: recurringIncomeTemplates?.length ? recurringIncomeTemplates : INITIAL_RECURRING_INCOME_TEMPLATES,
          goals: goals || [],
          contributions: contributions || [],
          customCategories: customCategories || [],
        };
        dispatchRef.current({ type: "HYDRATE", payload: hydrateState });
      } catch (err) {
        console.error("Error loading data from Supabase:", err);
        setDataError(err.message || "Error al cargar datos");
      } finally {
        setIsDataLoading(false);
      }
    };
    load();
  }, [userId]);

  // Dispatch que sincroniza con Supabase (intercepta acciones)
  const syncDispatch = useCallback(
    async (action) => {
      if (!userId) {
        dispatchRef.current(action);
        return;
      }
      const d = dispatchRef.current;
      try {
        switch (action.type) {
          case "CREATE_MONTH": {
            const month = await supabaseApi.createMonth(userId, {
              name: action.payload.name,
              monthNumber: action.payload.monthNumber,
              year: action.payload.year,
            });
            d({ type: "CREATE_MONTH", payload: month });
            return month;
          }
          case "DELETE_MONTH":
            await supabaseApi.deleteMonth(userId, action.payload);
            d(action);
            return;
          case "ADD_INCOME": {
            const income = await supabaseApi.addIncome(userId, action.payload);
            d({ type: "ADD_INCOME", payload: income });
            return income;
          }
          case "UPDATE_INCOME":
            await supabaseApi.updateIncome(userId, {
              incomeId: action.payload.incomeId,
              name: action.payload.name,
              amount: action.payload.amount,
            });
            d(action);
            return;
          case "DELETE_INCOME":
            await supabaseApi.deleteIncome(userId, action.payload.incomeId);
            d(action);
            return;
          case "ADD_EXPENSE": {
            const expense = await supabaseApi.addExpense(userId, {
              monthId: action.payload.monthId,
              name: action.payload.name,
              amount: action.payload.amount,
              category: action.payload.category,
              status: action.payload.status,
              dueDay: action.payload.dueDay,
              isFromRecurring: action.payload.isFromRecurring,
              recurringTemplateId: action.payload.recurringTemplateId,
              remindDaysBefore: action.payload.remindDaysBefore,
              date: action.payload.date,
              paidAt: action.payload.paidAt,
            });
            d({ type: "ADD_EXPENSE", payload: expense });
            return expense;
          }
          case "UPDATE_EXPENSE":
            await supabaseApi.updateExpense(userId, action.payload.id, action.payload.patch);
            d(action);
            return;
          case "DELETE_EXPENSE":
            await supabaseApi.deleteExpense(userId, action.payload);
            d(action);
            return;
          case "TOGGLE_EXPENSE_PAID": {
            const expense = await supabaseApi.toggleExpensePaid(userId, action.payload);
            d({ type: "UPDATE_EXPENSE", payload: { id: action.payload, patch: { status: expense.status, paidAt: expense.paidAt } } });
            return;
          }
          case "CREATE_RECURRING_TEMPLATE": {
            const tpl = await supabaseApi.createRecurringTemplate(userId, {
              name: action.payload.name,
              amount: action.payload.amount,
              category: action.payload.category,
              dueDay: action.payload.dueDay,
              active: action.payload.active,
              remindDaysBefore: action.payload.remindDaysBefore,
            });
            d({ type: "CREATE_RECURRING_TEMPLATE", payload: tpl });
            return tpl;
          }
          case "UPDATE_RECURRING_TEMPLATE":
            await supabaseApi.updateRecurringTemplate(userId, action.payload.id, action.payload.patch);
            d(action);
            return;
          case "DELETE_RECURRING_TEMPLATE":
            await supabaseApi.deleteRecurringTemplate(userId, action.payload);
            d(action);
            return;
          case "TOGGLE_RECURRING_TEMPLATE": {
            const t = state.recurringTemplates.find((x) => x.id === action.payload);
            if (t) {
              const updated = await supabaseApi.updateRecurringTemplate(userId, action.payload, { active: !t.active });
              d({ type: "UPDATE_RECURRING_TEMPLATE", payload: { id: action.payload, patch: { active: updated.active } } });
            }
            return;
          }
          case "CREATE_RECURRING_INCOME_TEMPLATE": {
            const tpl = await supabaseApi.createRecurringIncomeTemplate(userId, action.payload);
            d({ type: "CREATE_RECURRING_INCOME_TEMPLATE", payload: tpl });
            return tpl;
          }
          case "UPDATE_RECURRING_INCOME_TEMPLATE":
            await supabaseApi.updateRecurringIncomeTemplate(userId, action.payload.id, action.payload.patch);
            d(action);
            return;
          case "DELETE_RECURRING_INCOME_TEMPLATE": {
            const templateId = action.payload;
            try {
              await supabaseApi.deleteRecurringIncomeTemplate(userId, templateId);
            } catch (err) {
              // Plantillas default (inc_t1, inc_t2) nunca estuvieron en Supabase
              if (!/^inc_t\d+$/.test(templateId)) throw err;
            }
            d(action);
            return;
          }
          case "TOGGLE_RECURRING_INCOME_TEMPLATE": {
            const t = state.recurringIncomeTemplates.find((x) => x.id === action.payload);
            if (t) {
              try {
                const updated = await supabaseApi.updateRecurringIncomeTemplate(userId, action.payload, { active: !t.active });
                d({ type: "UPDATE_RECURRING_INCOME_TEMPLATE", payload: { id: action.payload, patch: { active: updated.active } } });
              } catch (err) {
                if (!/^inc_t\d+$/.test(action.payload)) throw err;
                d({ type: "UPDATE_RECURRING_INCOME_TEMPLATE", payload: { id: action.payload, patch: { active: !t.active } } });
              }
            }
            return;
          }
          case "CREATE_GOAL": {
            const goal = await supabaseApi.createGoal(userId, action.payload);
            d({ type: "CREATE_GOAL", payload: goal });
            return goal;
          }
          case "UPDATE_GOAL":
            await supabaseApi.updateGoal(userId, action.payload.id, action.payload.patch);
            d(action);
            return;
          case "DELETE_GOAL":
            await supabaseApi.deleteGoal(userId, action.payload);
            d(action);
            return;
          case "ADD_CONTRIBUTION": {
            const contrib = await supabaseApi.addContribution(userId, {
              goalId: action.payload.goalId,
              monthId: action.payload.monthId,
              amount: action.payload.amount,
              date: action.payload.date,
              note: action.payload.note,
            });
            d({ type: "ADD_CONTRIBUTION", payload: contrib });
            return contrib;
          }
          case "DELETE_CONTRIBUTION":
            await supabaseApi.deleteContribution(userId, action.payload);
            d(action);
            return;
          case "SET_CURRENCY":
          case "SET_EXCHANGE_RATE":
          case "SET_EXCHANGE_RATES":
          case "SET_SHOW_AMOUNTS":
          case "SET_ENABLED_CURRENCIES": {
            d(action);
            let nextCurrency = state.currency;
            if (action.type === "SET_CURRENCY") nextCurrency = action.payload;
            else if (action.type === "SET_ENABLED_CURRENCIES") {
              const codes = action.payload;
              nextCurrency = codes.includes(state.currency) ? state.currency : (codes[0] || "PEN");
            }
            const next = {
              currency: nextCurrency,
              usdToPenRate: state.exchangeRates?.PEN ?? state.usdToPenRate,
              exchangeRates: action.type === "SET_EXCHANGE_RATES" ? action.payload : state.exchangeRates,
              showAmounts: action.type === "SET_SHOW_AMOUNTS" ? action.payload : state.showAmounts,
              enabledCurrencies: action.type === "SET_ENABLED_CURRENCIES" ? action.payload : state.enabledCurrencies,
            };
            if (action.type === "SET_EXCHANGE_RATE") next.usdToPenRate = action.payload;
            await supabaseApi.upsertUserSettings(userId, next);
            return;
          }
          case "CREATE_CUSTOM_CATEGORY": {
            const cat = await supabaseApi.createCustomCategory(userId, { name: action.payload.name });
            d({ type: "CREATE_CUSTOM_CATEGORY", payload: cat });
            return cat;
          }
          case "UPDATE_CUSTOM_CATEGORY":
            await supabaseApi.updateCustomCategory(userId, action.payload.id, { name: action.payload.name });
            d(action);
            return;
          case "DELETE_CUSTOM_CATEGORY":
            await supabaseApi.deleteCustomCategory(userId, action.payload.id);
            d(action);
            return;
          default:
            d(action);
            return;
        }
      } catch (err) {
        console.error("Supabase sync error:", err);
      }
    },
    [userId, state.currency, state.usdToPenRate, state.exchangeRates, state.showAmounts, state.enabledCurrencies, state.recurringTemplates, state.recurringIncomeTemplates]
  );

  syncDispatchRef.current = syncDispatch;

  const apiKey = process.env.REACT_APP_EXCHANGERATE_API_KEY;

  useEffect(() => {
    if (!apiKey?.trim()) return;
    fetchExchangeRates(apiKey).then((result) => {
      if (result?.rates && syncDispatchRef.current) {
        syncDispatchRef.current({ type: "SET_EXCHANGE_RATES", payload: result.rates });
      }
    });
  }, [apiKey]);

  const refreshExchangeRate = useCallback(async () => {
    if (!apiKey?.trim()) return false;
    const result = await forceFetchExchangeRates(apiKey);
    if (result?.rates) {
      syncDispatch({ type: "SET_EXCHANGE_RATES", payload: result.rates });
      return true;
    }
    return false;
  }, [apiKey, syncDispatch]);

  // ========== SELECTORES ==========
  const getMonthById = (id) => {
    return state.months.find(m => m.id === id);
  };

  const getMonthIncomes = (monthId) => {
    return state.incomes.filter(i => i.monthId === monthId);
  };

  const getMonthExpenses = (monthId) => {
    return state.expenses.filter(e => e.monthId === monthId);
  };

  const getMonthContributions = (monthId) => {
    return state.contributions.filter(c => c.monthId === monthId);
  };

  const getMonthTotals = (monthId) => {
    const incomes = getMonthIncomes(monthId);
    const expenses = getMonthExpenses(monthId);
    const contributions = getMonthContributions(monthId);

    // Total de ingresos
    const income = incomes.reduce((sum, i) => sum + i.amount, 0);
    
    // Separar gastos pagados y pendientes
    const paidExpenses = expenses.filter(e => e.status === "PAID");
    const pendingExpenses = expenses.filter(e => e.status === "PENDING");
    
    // Calcular totales según reglas de negocio
    const totalPaid = paidExpenses.reduce((sum, e) => sum + e.amount, 0);
    const totalPending = pendingExpenses.reduce((sum, e) => sum + e.amount, 0);
    const totalCommitted = totalPaid + totalPending; // Total comprometido
    
    // Ahorro
    const savings = contributions.reduce((sum, c) => sum + c.amount, 0);
    
    // Restante disponible (solo considera gastos PAGADOS)
    const availableRemaining = income - totalPaid - savings;
    
    // Restante final (informativo - si pagaras todo)
    const finalRemaining = income - totalCommitted - savings;

    return {
      income,                    // Total ingresos
      totalPaid,                 // Gastos ya pagados
      totalPending,              // Gastos pendientes
      totalCommitted,            // Total comprometido (pagados + pendientes)
      savings,                   // Ahorro
      availableRemaining,        // Restante disponible REAL
      finalRemaining,            // Restante final (informativo)
      // Mantener compatibilidad con código existente
      expenses: totalCommitted,  // Total de gastos (legacy)
      remaining: availableRemaining  // Restante (legacy)
    };
  };

  const getMonthPendingBills = (monthId) => {
    return state.expenses.filter(
      e => e.monthId === monthId && e.isFromRecurring && e.status === "PENDING"
    );
  };

  const getEstimatedRecurringTotal = () => {
    return state.recurringTemplates
      .filter(t => t.active)
      .reduce((sum, t) => sum + t.amount, 0);
  };

  const getExpenseById = (id) => {
    return state.expenses.find(e => e.id === id);
  };

  const getGoalCurrentAmount = (goalId) => {
    return state.contributions
      .filter(c => c.goalId === goalId)
      .reduce((sum, c) => sum + c.amount, 0);
  };

  const monthExists = (monthNumber, year) => {
    return state.months.some(m => m.monthNumber === monthNumber && m.year === year);
  };

  // Gastos pendientes que vencen pronto (según remindDaysBefore) - solo del mes actual
  const getUpcomingBills = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();

    const currentMonthIds = state.months
      .filter(m => m.year === currentYear && m.monthNumber === currentMonth)
      .map(m => m.id);

    return state.expenses
      .filter(e => e.status === "PENDING" && e.dueDay != null && currentMonthIds.includes(e.monthId))
      .map(expense => {
        const remindDays = expense.remindDaysBefore ?? 0;
        const dueDay = expense.dueDay;
        const daysUntilDue = dueDay - currentDay;
        const shouldRemind = daysUntilDue <= remindDays && daysUntilDue >= -1;
        return { ...expense, shouldRemind, daysUntilDue };
      })
      .filter(e => e.shouldRemind)
      .sort((a, b) => a.daysUntilDue - b.daysUntilDue);
  };

  // Meses ordenados por fecha descendente (más reciente primero)
  const getMonthsSortedByDate = () => {
    return [...state.months].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.monthNumber - a.monthNumber;
    });
  };

  const rates = state.exchangeRates || DEFAULT_EXCHANGE_RATES;
  const penRate = rates.PEN || state.usdToPenRate || 3.75;
  const displayRate = rates[state.currency] ?? (state.currency === "USD" ? 1 : penRate);

  const toDisplayAmount = (amountInPen) => {
    if (!amountInPen || isNaN(amountInPen)) return 0;
    if (state.currency === "PEN" || !displayRate) return amountInPen;
    return amountInPen * (displayRate / penRate);
  };

  const toStorageAmount = (amountFromUser) => {
    const n = parseFloat(amountFromUser);
    if (isNaN(n)) return 0;
    if (state.currency === "PEN" || !displayRate) return Math.round(n);
    return n * (penRate / displayRate);
  };

  // Formatear monto para mostrar (convierte desde PEN y formatea)
  // Si hideValue no se pasa, usa la preferencia global showAmounts
  const formatDisplayAmount = (amountInPen, hideValue) => {
    const shouldHide = hideValue !== undefined ? hideValue : !state.showAmounts;
    if (shouldHide) return "••••";
    const displayAmount = toDisplayAmount(amountInPen);
    const useDecimals = state.currency === "USD";
    const formatted = new Intl.NumberFormat("es-PE", {
      minimumFractionDigits: useDecimals ? 2 : 0,
      maximumFractionDigits: useDecimals ? 2 : 0,
    }).format(displayAmount);
    return `${state.currencySymbol} ${formatted}`;
  };

  const formatUserAmount = (amount) => {
    const n = parseFloat(amount);
    if (isNaN(n)) return `${state.currencySymbol} 0`;
    const useDecimals = state.currency === "USD";
    const formatted = new Intl.NumberFormat("es-PE", {
      minimumFractionDigits: useDecimals ? 2 : 0,
      maximumFractionDigits: useDecimals ? 2 : 0,
    }).format(n);
    return `${state.currencySymbol} ${formatted}`;
  };

  const value = {
    // Estado
    state,
    dispatch: syncDispatch,
    isDataLoading,
    dataError,
    // Tipo de cambio API
    hasExchangeRateApiKey: !!apiKey?.trim(),
    refreshExchangeRate,
    getCachedRateInfo,
    // Moneda y conversión
    toDisplayAmount,
    toStorageAmount,
    formatDisplayAmount,
    formatUserAmount,
    // Selectores
    getMonthById,
    getMonthIncomes,
    getMonthExpenses,
    getMonthContributions,
    getMonthTotals,
    getMonthPendingBills,
    getEstimatedRecurringTotal,
    getExpenseById,
    getGoalCurrentAmount,
    monthExists,
    getUpcomingBills,
    getMonthsSortedByDate
  };

  return (
    <ControlCashContext.Provider value={value}>
      {children}
    </ControlCashContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================
export const useControlCash = () => {
  const context = useContext(ControlCashContext);
  if (!context) {
    throw new Error("useControlCash must be used within ControlCashProvider");
  }
  return context;
};
